import React, { useRef, useState } from 'react';

interface VideoPreviewProps {
  taskName: string;
  videoUrl: string | null;
  duration: number;
  isLandscape: boolean;
  onRetake: () => void;
  onUseVideo: () => void;
  onClose: () => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({
  taskName,
  videoUrl,
  duration,
  onRetake,
  onUseVideo,
  onClose,
}) => {
  const previewRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (!previewRef.current || !videoUrl) return;
    if (isPlaying) {
      previewRef.current.pause();
    } else {
      previewRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="vr-screen">
      {/* Header */}
      <div className="vr-header">
        <button className="vr-close-btn" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <p className="vr-prompt">{taskName}</p>
      </div>

      {/* Video preview area */}
      <div className="vr-preview">
        <div className="vr-video-wrap">
          {videoUrl && (
            <video
              ref={previewRef}
              src={videoUrl}
              className="vr-video"
              playsInline
              onClick={togglePlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            />
          )}

          {/* Play/pause button overlay — always visible so user can stop playback */}
          <button
            className={`vr-play-btn${isPlaying ? ' vr-play-btn--playing' : ''}`}
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <polygon points="8,5 19,12 8,19" />
              </svg>
            )}
          </button>

          {/* Preview badge */}
          <div className="vr-preview-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
            Preview
          </div>
        </div>

        <div className="vr-duration">{formatTime(duration)} recorded</div>
      </div>

      {/* Action buttons */}
      <div className="vr-actions">
        <button className="vr-retake-btn" onClick={onRetake}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round">
            <polyline points="1,4 1,10 7,10" />
            <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
          </svg>
          Retake
        </button>
        <button className="vr-use-btn" onClick={onUseVideo}>
          USE VIDEO
        </button>
      </div>
    </div>
  );
};
